/**
 * LE PORT D'UN FIL DE CONVERSATION, tel que `FilDeConversation` et
 * `ComposeurConversation` le recoivent. `@taki/ui` ne parle jamais au SDK :
 * c'est l'hote qui passe `sdk.messages`, qui satisfait ce port tel quel
 * (memes signatures, `packages/sdk/src/messages.ts`).
 */
import type { SuiviLiaVue } from './attentesLia.js';
import type { CleDeReaction, ReactionsDuMessage } from './reactions.js';

/** Un message tel que la vue le lit (structurel : le `Message` du SDK en est un). */
export interface MessageVue {
  id: string;
  conversationId?: string | undefined;
  parentId?: string | undefined;
  /** Un personId, ou `lia`, ou `systeme`. */
  auteur?: string | undefined;
  texte?: string | undefined;
  createdAt: Date;
  createdBy: string;
  mentions?: { personnes: string[]; tous: boolean; lia: boolean } | undefined;
  signeLia?: { mentionnePar: string; journalId?: string | undefined } | undefined;
  systeme?: { evenement: string; details?: string | undefined } | undefined;
  /** Qui a reagi, par cle (`reactions.ts`). */
  reactions?: ReactionsDuMessage | undefined;
}

export interface PortDeFil<M extends MessageVue = MessageVue> {
  /** Les `limite` derniers messages de la conversation, du plus ancien au plus recent. */
  observerConversation(
    conversationId: string,
    options: { limite?: number },
    /** `meta.local` : ce rendu porte un envoi de CE client pas encore ecrit au serveur (V3-1). */
    onChange: (messages: M[], meta?: { local: boolean }) => void,
    onError?: (error: unknown) => void,
  ): () => void;
  /** La racine et ses reponses, du plus ancien au plus recent. */
  observerFil(
    conversationId: string,
    parentId: string,
    onChange: (messages: M[]) => void,
    onError?: (error: unknown) => void,
  ): () => void;
  /**
   * Les reponses de Lia EN COURS dans la conversation (le suivi
   * `lia-<messageId>` que le serveur pose des qu'il voit @Lia), rendues en
   * entier a chaque changement. Absent : pas de bulle « Lia au travail ».
   */
  observerSuivisLia?(
    conversationId: string,
    onChange: (suivis: SuiviLiaVue[]) => void,
    onError?: (error: unknown) => void,
  ): () => void;
  envoyer(conversationId: string, texte: string, options?: { parentId?: string }): Promise<unknown>;
  /** Pose la reaction de la personne connectee. Absent (avec `retirerReaction`) : pas de reactions. */
  reagir?(messageId: string, reaction: CleDeReaction): Promise<void>;
  /** Retire SA reaction, jamais celle d'un autre. */
  retirerReaction?(messageId: string, reaction: CleDeReaction): Promise<void>;
}

/** Les auteurs reserves (recopie de `@taki/data-model`, que ce package n'importe pas). */
export const AUTEUR_LIA = 'lia';
export const AUTEUR_SYSTEME = 'systeme';

/**
 * LE MESSAGE A ACQUITTER apres un rendu du fil (V3-1 du 07/10/2026) : le
 * dernier message date, SAUF si le rendu porte encore un envoi de ce poste
 * pas ecrit au serveur (`meta.local`). Ce message n'existe pas encore en
 * base : le relire pour acquitter faisait refuser la lecture (« Null value
 * error »), dans un direct, un canal ou le chatter d'une fiche, des le premier
 * envoi. Le rendu confirme qui suit rend le bon message. Pure, testee.
 */
export function messageAAcquitter<M extends Pick<MessageVue, 'id' | 'createdAt'>>(
  messages: readonly M[],
  meta?: { local: boolean },
): { id: string; date: Date } | null {
  if (meta?.local === true) return null;
  const dernier = messages.filter((m) => Number.isFinite(m.createdAt.getTime())).at(-1);
  return dernier === undefined ? null : { id: dernier.id, date: dernier.createdAt };
}
